// ============================================================================
// DAS NOTIFICATION SERVICE
// ============================================================================
// Real-time notification handling for DAS tickets

import { get, post } from '../client/axiosClient';
import type { ApiResponse, ListResponse, ID, DASNotification } from '../types';

/**
 * DAS Notification Service
 * Handles all API operations related to notifications
 */
class DASNotificationService {
  // ============================================================================
  // NOTIFICATION OPERATIONS
  // ============================================================================

  /**
   * Get all notifications for current user
   */
  async getAll(unreadOnly = false): Promise<ListResponse<DASNotification>> {
    const endpoint = unreadOnly
      ? '/v1/das/notifications?unreadOnly=true'
      : '/v1/das/notifications';

    const response = await get<ListResponse<DASNotification>>(endpoint);
    return response.data;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<ApiResponse<{ unreadCount: number }>> {
    const response = await get<ApiResponse<{ unreadCount: number }>>('/v1/das/notifications/unread-count');
    return response.data;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: ID): Promise<ApiResponse<void>> {
    const response = await post<ApiResponse<void>>(
      `/v1/das/notifications/${notificationId}/mark-read`,
      {}
    );
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<void>> {
    const response = await post<ApiResponse<void>>('/v1/das/notifications/mark-all-read', {});
    return response.data;
  }

  /**
   * Delete notification
   */
  async delete(notificationId: ID): Promise<ApiResponse<void>> {
    const { del } = await import('../client/axiosClient');
    const response = await del<ApiResponse<void>>(
      `/v1/das/notifications/${notificationId}`
    );
    return response.data;
  }

  /**
   * Get notifications for a specific ticket
   */
  async getByTicket(ticketId: ID): Promise<ListResponse<DASNotification>> {
    const response = await get<ListResponse<DASNotification>>(
      `/v1/das/notifications/ticket/${ticketId}`
    );
    return response.data;
  }
}

// Export singleton instance
export const dasNotificationService = new DASNotificationService();
export default dasNotificationService;
